import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { GenericDocumentDto, DocumentScopeEnum, GenericColors } from 'api/contract';
import GenericBadge from 'components/ui/Badge';
import { GenericLoading } from 'components/ui/Loader';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { format } from 'date-fns';
import { scopeLabels } from 'shared/constants/scopeLabels';
import { LucideIcon } from 'lucide-react';
import { CardTableColumn } from 'components/display/Card/CardTable/types';
import { CardTableCollapsible } from 'components/display/Card/CardTable/CardTableCollapsible';
import { useDocumentViewerDialog } from 'components/domain/Document/useDocumentViewerDialog';
import { scopeIcons } from 'features/contract-view/components/contract-details/contract-documents/ContractDocuments';

interface GroupContractDocumentsProps {
  /** Documents chargés par le parent (fetch au mount du détail, badge inclus). */
  documents: GenericDocumentDto[];
  isLoading: boolean;
}

interface DocumentsByScope {
  scope: DocumentScopeEnum;
  title: string;
  icon: LucideIcon;
  documents: GenericDocumentDto[];
}

const scopeOrder: Record<string, number> = {
  [DocumentScopeEnum.Contract]: 1,
  [DocumentScopeEnum.Product]: 2,
  [DocumentScopeEnum.Subscriber]: 3,
  [DocumentScopeEnum.System]: 4,
};

/**
 * Onglet Documents du détail d'un contrat collectif : même rendu que l'onglet
 * documents du contrat individuel (groupes par scope), alimenté par
 * GET /contrats/groupes/{id}/documents (contrat généré/signé + docs produit).
 */
export const GroupContractDocuments: FC<GroupContractDocumentsProps> = ({ documents, isLoading }) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common, NsKey.Document]);
  const { openDocumentViewer } = useDocumentViewerDialog();

  const documentsByScope = useMemo(() => {
    const items = documents.reduce((acc, current) => {
      const index = acc.findIndex(item => item.scope === current.scope);
      if (acc[index]) {
        acc[index].documents.push(current);
      } else {
        acc.push({
          scope: current.scope,
          title: scopeLabels[current.scope],
          icon: scopeIcons[current.scope],
          documents: [current]
        });
      }
      return acc;
    }, [] as DocumentsByScope[]);
    items.sort((a, b) => (scopeOrder[a.scope] ?? 99) - (scopeOrder[b.scope] ?? 99));
    return items;
  }, [documents]);

  const handleDocumentClick = (document: GenericDocumentDto) => {
    if (!document.storedFileId) {
      return;
    }

    openDocumentViewer({
      title: t(document.label) || 'Document',
      storedFileId: document.storedFileId,
    });
  };

  const renderStatus = useCallback((label?: string, color?: GenericColors) => {
    if (label) {
      return <GenericBadge
        label={label}
        color={color ? color : GenericColors.Gray}
        badgeType="sm-bold"
      />
    } else {
      return '_';
    }
  }, []);

  const columns: CardTableColumn<GenericDocumentDto>[] = [
    {
      key: 'document',
      label: 'Document',
      width: 'col-span-5',
      render: (document) => (
        <div className="flex items-start justify-center flex-col">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDocumentClick(document);
              }}
              className="text-primary font-semibold hover:text-primary hover:underline transition-colors"
              disabled={!document.storedFileId}
            >
              {t(document.label)}
            </button>
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
              v{document.version}
            </span>
          </div>
          {document.signatureDate && (
            <p className='text-xs text-gray-400 mt-1'>
              {`${t('COMMON.LABELS.SIGNED_AT')} ${format(new Date(document.signatureDate), "dd MMM yyyy HH:mm")}`}
            </p>
          )}
        </div>
      )
    },
    {
      key: 'creationDate',
      label: 'Date création',
      width: 'col-span-3',
      render: (document) => (
        <div className="text-gray-600 text-sm">
          {new Date(document.creationDate).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      width: 'col-span-3',
      render: (document) => renderStatus(document.statusLabel, document.statusColor)
    }
  ];

  if (isLoading) {
    return (
      <div className="relative flex justify-center items-center p-8">
        <GenericLoading isParentSized />
      </div>
    );
  }

  if (documentsByScope.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-gray-500 bg-gray-50/50 rounded-xl border border-gray-100">
        Aucun document contractuel pour le moment.
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {documentsByScope.map((scope, scopeIndex) => (
        <CardTableCollapsible
          key={`scope-${scope.scope}-${scopeIndex}`}
          title={t(scope.title)}
          icon={scope.icon}
          defaultExpanded={scope.scope === DocumentScopeEnum.Contract}
          titleAside={
            <span className="text-xs font-medium text-gray-500 bg-white px-3 py-1 rounded-full">
              {scope.documents.length} document{scope.documents.length > 1 ? 's' : ''}
            </span>
          }
          columns={columns}
          data={scope.documents}
          actions={(document) => (
            <Button
              round
              color='slate'
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                handleDocumentClick(document);
              }}
              disabled={!document.storedFileId}
            >
              <EyeIcon className="w-5 h-5" />
            </Button>
          )}
          emptyStateMessage="Aucun document trouvé"
        />
      ))}
    </div>
  );
};
